import { useTranslation } from 'react-i18next';

import { QDivider, QStack, QText } from '@captivateiq/quotient';

import type { AppActionConfig } from '@captivateiq/assistant/types';

import { MessageButtonBar } from './MessageButtonBar';

type AppActionLabelProps = {
  actions: AppActionConfig[];
};

export const AppActionLabel = ({ actions }: AppActionLabelProps) => {
  const { t } = useTranslation('assistant');

  if (actions.length === 0) {
    return null;
  }

  return (
    <>
      {actions.map((action) => {
        const key = `app_actions.${action.name}`;
        const label = t(key);
        // If there is no explicit label for the action, skip it.
        if (label === key) {
          return null;
        }

        const buttons = action.buttons ?? [];
        const position = action.buttonPosition ?? 'right';
        const divider = (
          <QDivider sx={{ my: 0.5, ...(position === 'left' || position === 'right' ? { flex: 1 } : {}) }}>
            <QText color="text.secondary" variant="smallRegular">
              {label}
            </QText>
          </QDivider>
        );

        if (buttons.length === 0) {
          return <div key={action.name}>{divider}</div>;
        }

        const buttonBar = <MessageButtonBar buttons={buttons} />;

        if (position === 'top') {
          return (
            <QStack key={action.name} spacing="4px">
              {buttonBar}
              {divider}
            </QStack>
          );
        }
        if (position === 'bottom') {
          return (
            <QStack key={action.name} spacing="4px">
              {divider}
              {buttonBar}
            </QStack>
          );
        }

        return (
          <QStack alignItems="center" direction="row" key={action.name} spacing="4px">
            {position === 'left' && buttonBar}
            {divider}
            {position === 'right' && buttonBar}
          </QStack>
        );
      })}
    </>
  );
};
